/**
 * Offline Services Index
 *
 * Central export point for all offline services.
 * Provides factory function for getting services by entity type.
 */

// Export base class
export { BaseOfflineService } from './BaseOfflineService';

// Export entity services
export {
  TransactionOfflineService,
  transactionOfflineService,
} from './TransactionOfflineService';
export { ContactOfflineService, contactOfflineService } from './ContactOfflineService';
export { PayableOfflineService, payableOfflineService } from './PayableOfflineService';
export {
  ReceivableOfflineService,
  receivableOfflineService,
} from './ReceivableOfflineService';
export {
  InventoryOfflineService,
  inventoryOfflineService,
} from './InventoryOfflineService';

// Import types
import type { EntityType } from '../types/offline.types';
import { transactionOfflineService } from './TransactionOfflineService';
import { contactOfflineService } from './ContactOfflineService';
import { payableOfflineService } from './PayableOfflineService';
import { receivableOfflineService } from './ReceivableOfflineService';
import { inventoryOfflineService } from './InventoryOfflineService';

// ============================================
// SERVICE REGISTRY
// ============================================

/**
 * Registry of all offline services by entity type
 */
const serviceRegistry = {
  transaction: transactionOfflineService,
  contact: contactOfflineService,
  payable: payableOfflineService,
  receivable: receivableOfflineService,
  inventory: inventoryOfflineService,
  // TODO: Add remaining services as they are implemented
  // employee: employeeOfflineService,
  // branch: branchOfflineService,
  // transactionCategory: transactionCategoryOfflineService,
  // compositeItem: compositeItemOfflineService,
} as const;

/**
 * Get offline service by entity type
 */
export function getOfflineService<T extends keyof typeof serviceRegistry>(
  entityType: T
): (typeof serviceRegistry)[T] {
  const service = serviceRegistry[entityType];
  if (!service) {
    throw new Error(`No offline service found for entity type: ${entityType}`);
  }
  return service;
}

/**
 * Sync all entities from server
 * Call this when the app comes online to refresh all cached data
 */
export async function syncAllFromServer(): Promise<void> {
  console.log('[OfflineServices] Starting full sync from server...');

  const services = Object.values(serviceRegistry);

  // Sync in parallel for speed
  await Promise.all(
    services.map(async (service) => {
      try {
        await service.syncFromServer();
      } catch (error) {
        console.error(`[OfflineServices] Failed to sync:`, error);
      }
    })
  );

  console.log('[OfflineServices] Full sync completed');
}

/**
 * Get pending items count across all services
 */
export async function getTotalPendingCount(): Promise<number> {
  const services = Object.values(serviceRegistry);
  const counts = await Promise.all(
    services.map(async (service) => {
      const items = await service.getPendingQueueItems();
      return items.length;
    })
  );
  return counts.reduce((sum, count) => sum + count, 0);
}

/**
 * Get failed items count across all services
 */
export async function getTotalFailedCount(): Promise<number> {
  const services = Object.values(serviceRegistry);
  const counts = await Promise.all(
    services.map(async (service) => {
      const items = await service.getFailedQueueItems();
      return items.length;
    })
  );
  return counts.reduce((sum, count) => sum + count, 0);
}

/**
 * Retry all failed items across all services
 */
export async function retryAllFailed(): Promise<void> {
  const services = Object.values(serviceRegistry);
  await Promise.all(
    services.map((service) => service.retryFailedItems())
  );
}

/**
 * Clear all offline caches
 * Use with caution - typically only on logout
 */
export async function clearAllCaches(): Promise<void> {
  const services = Object.values(serviceRegistry);
  await Promise.all(
    services.map((service) => service.clearCache())
  );
}
